/**
 * In-place zoom for product gallery images.
 *
 * Two modes, both driven by an overlay layer painted with a high-resolution
 * copy of the image and panned via background-position:
 *   hover  — magnifies while the cursor is over the image
 *   inline — click to zoom, move to pan, click again to reset
 *
 * Both are pointer-driven. A coarse pointer has no hover state and its drag
 * gesture belongs to the slider, so the element stays inert on touch devices.
 */
class ProductMediaZoom extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.mode = this.dataset.zoomMode === 'inline' ? 'inline' : 'hover';
    this.level = Number(this.dataset.zoomLevel) || 2;
    this.src = this.dataset.zoomSrc;

    if (!this.src || !this.querySelector('img')) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.initialized = true;
    this.isZoomed = false;

    this.layer = document.createElement('div');
    this.layer.className = 'product__media-zoom-layer';
    this.layer.style.backgroundSize = `${this.level * 100}%`;
    this.appendChild(this.layer);

    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onClick = this.onClick.bind(this);

    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.addEventListener('dragstart', event => event.preventDefault());

    if (this.mode === 'hover') {
      this.addEventListener('pointerenter', this.onPointerEnter);
    } else {
      this.addEventListener('click', this.onClick);
    }
  }

  disconnectedCallback() {
    this.deactivate();
  }

  onPointerEnter(event) {
    this.activate(event);
  }

  onPointerLeave() {
    this.deactivate();
  }

  onClick(event) {
    if (this.isZoomed) {
      this.deactivate();
    } else {
      this.activate(event);
    }
  }

  onPointerMove(event) {
    if (!this.isZoomed) return;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.pan(event.clientX, event.clientY));
  }

  pan(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const clamp = value => Math.min(100, Math.max(0, value));
    const x = clamp(((clientX - rect.left) / rect.width) * 100);
    const y = clamp(((clientY - rect.top) / rect.height) * 100);

    this.layer.style.backgroundPosition = `${x}% ${y}%`;
  }

  /** Fetches the high-resolution image once, so the layer never paints half-loaded. */
  loadZoomImage() {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = resolve;
      image.onerror = reject;
      image.src = this.src;
    })
      .then(() => {
        this.layer.style.backgroundImage = `url("${this.src}")`;
      })
      .catch(() => {
        this.deactivate();
        this.loadPromise = Promise.reject();
        this.loadPromise.catch(() => {});
      });

    return this.loadPromise;
  }

  activate(event) {
    if (this.isZoomed) return;
    this.isZoomed = true;
    this.pan(event.clientX, event.clientY);

    this.loadZoomImage().then(
      () => {
        if (this.isZoomed) this.classList.add('is-zoomed');
      },
      () => {}
    );
  }

  deactivate() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.isZoomed = false;
    this.classList.remove('is-zoomed');
  }
}

if (!customElements.get('product-media-zoom')) {
  customElements.define('product-media-zoom', ProductMediaZoom);
}
